/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, January 26th 2022, 9:52:51 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { CSSProperties } from "react";
import { XYCoord } from "react-dnd";
import { Identifier } from "dnd-core";
import { useDragLayer } from "react-dnd";

interface UseOSCustomDragLayerSpec {
  itemType: Identifier;
}

/**
 * A hook that wires the calling component into the DnD system as a drag layer but unlike the default
 * useDragLayer hook, this one optimizes performance by only collecting state/props if the drag event
 * is for the specific item type passed to the hook. This avoids issues where multiple drag layer monitor
 * state that they are not concerened with and thus eliminates the unecessary re-renders from the monitored
 * state.
 */
export const useOSCustomDragLayer = <T,>({ itemType }: UseOSCustomDragLayerSpec) => {
  const collectedProps = useDragLayer<DragLayerCollectedProps<T>>((monitor) => {
    if (monitor.getItemType() === itemType)
      return {
        item: monitor.getItem(),
        itemType,
        initialOffset: monitor.getInitialSourceClientOffset(),
        currentOffset: monitor.getSourceClientOffset(),
        isDraggingEvent: monitor.isDragging(),
      };
  });

  return { collectedProps };
};

const layerStyles: CSSProperties = {
  position: "fixed",
  pointerEvents: "none",
  zIndex: 100,
  left: 0,
  top: 0,
  width: "100%",
  height: "100%",
};

export interface DragLayerCollectedProps<DraggedItemState> {
  item: DraggedItemState;
  itemType: Identifier | null;
  initialOffset: XYCoord | null;
  currentOffset: XYCoord | null;
  isDraggingEvent: boolean;
}

const getDragLayerItemStyles = <DraggedItemState,>(props: DragLayerCollectedProps<DraggedItemState>) => {
  const { initialOffset, currentOffset } = props;
  if (!initialOffset || !currentOffset) {
    return {
      display: "none",
    };
  }

  let { x, y } = currentOffset;

  const transform = `translate(${x}px, ${y}px)`;

  return {
    transform,
    WebkitTransform: transform,
  };
};

interface CustomDragLayerProps<DraggedItemState> {
  children: React.ReactNode;
  collectedProps: DragLayerCollectedProps<DraggedItemState>;
  style?: React.CSSProperties;
}

export const CustomDragLayer = <DraggedItemState,>({
  children,
  collectedProps,
}: CustomDragLayerProps<DraggedItemState>) => {
  const isDraggingEvent = collectedProps?.isDraggingEvent;

  if (!isDraggingEvent) {
    return null;
  }

  return <div style={{ ...layerStyles, ...getDragLayerItemStyles(collectedProps) }}>{children}</div>;
};
